import React, { SyntheticEvent, useContext, useId, useMemo, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useTranslation } from 'react-i18next';
import { OpenStatusDropdownContext } from 'components/tasks/TasksViewer/TaskStatusDropdown';
import ProjectSearchMenu, { ProjectOption } from 'components/tasks/TasksViewer/ProjectSearchMenu';
import { DURATION_FORMAT, OBJECTIVE_UNIT_SUGGESTIONS, ObjectiveQuantityFormat } from './formatUnit';
import styles from './ObjectiveUnitDropdown.module.css';

interface UnitToggleProps {
  'aria-expanded'?: boolean;
  className?: string;
  onClick?: (event: SyntheticEvent) => void;
  unit?: string | null;
}

const UnitToggle = React.forwardRef<HTMLDivElement, UnitToggleProps>(({
  'aria-expanded': ariaExpanded,
  className,
  onClick,
  unit,
  ...props
}, ref) => {
  const { t } = useTranslation();
  const label = unit?.trim();

  return (
    <div
      {...props}
      aria-expanded={ariaExpanded}
      aria-haspopup="menu"
      aria-label={t('unit')}
      className={`${styles.toggle}${className ? ` ${className}` : ''}`}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        onClick?.(event);
      }}
      ref={ref}
      role="button"
      tabIndex={0}
    >
      {
        label
          ? label
          : <span className={styles.placeholder}>{t('noUnit')}</span>
      }
    </div>
  );
});

UnitToggle.displayName = 'UnitToggle';

const DURATION_OPTION_ID = '__duration__';

export interface ObjectiveQuantityChoice {
  format: ObjectiveQuantityFormat | null;
  unit: string | null;
}

interface ObjectiveUnitDropdownProps {
  format?: string | null;
  onChange: (choice: ObjectiveQuantityChoice) => void;
  unit?: string | null;
}

const ObjectiveUnitDropdown = ({
  format,
  onChange,
  unit,
}: ObjectiveUnitDropdownProps) => {
  const { t } = useTranslation();
  const dropdownId = useId();
  const exclusiveDropdown = useContext(OpenStatusDropdownContext);
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = exclusiveDropdown
    ? exclusiveDropdown.openDropdownId === dropdownId
    : internalOpen;
  const duration = format === DURATION_FORMAT;
  const selected = duration ? '' : (unit?.trim() || '');
  const options = useMemo(() => {
    const units = selected && !OBJECTIVE_UNIT_SUGGESTIONS.includes(selected)
      ? [selected, ...OBJECTIVE_UNIT_SUGGESTIONS]
      : [...OBJECTIVE_UNIT_SUGGESTIONS];
    return [
      { id: '', name: t('noUnit') },
      { id: DURATION_OPTION_ID, name: t('durationFormat') },
      ...units.map(choice => ({ id: choice, name: choice })),
    ];
  }, [selected, t]);

  const setOpen = (nextShow: boolean) => {
    if (exclusiveDropdown) {
      exclusiveDropdown.setOpenDropdownId(nextShow ? dropdownId : null);
      return;
    }
    setInternalOpen(nextShow);
  };

  const selectUnit = (option: ProjectOption) => {
    if (option.id === DURATION_OPTION_ID) {
      onChange({ format: DURATION_FORMAT, unit: null });
    } else {
      onChange({ format: null, unit: option.id || null });
    }
    setOpen(false);
  };

  return (
    <Dropdown
      autoClose="outside"
      className={styles.button}
      onToggle={(nextShow: boolean) => setOpen(nextShow)}
      show={isOpen}
    >
      <Dropdown.Toggle
        as={UnitToggle as React.ElementType}
        unit={duration ? t('durationFormat') : selected}
      />
      <Dropdown.Menu
        data-testid="objective-unit-menu"
        popperConfig={{
          modifiers: [{ name: 'hide', enabled: false }],
        }}
      >
        <ProjectSearchMenu
          isOpen={isOpen}
          onSelect={selectUnit}
          projects={options}
        />
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ObjectiveUnitDropdown;
